import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../../utils/api';
import { useJobsData, useInvoicesData } from '../../hooks/useData';
import { queryKeys } from '../../hooks/useData';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (invoice: any) => void;
};

const eligibleStatuses = new Set(['COMPLETED', 'IN_PROGRESS', 'AWAITING_FOLLOWUP']);

export function CreateInvoiceFromJobModal({ open, onClose, onCreated }: Props) {
  const { data: jobs = [] } = useJobsData();
  const { data: invoices = [] } = useInvoicesData();
  const qc = useQueryClient();

  const invoicedJobIds = useMemo(
    () =>
      new Set(
        ((invoices as any[]) || [])
          .filter((inv) => inv.jobId && !['VOIDED', 'CANCELLED'].includes(inv.status))
          .map((inv) => inv.jobId as string),
      ),
    [invoices],
  );
  const [selectedJobId, setSelectedJobId] = useState('');
  const [dueInDays, setDueInDays] = useState<number>(14);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Payment due within 14 days of invoice date.');
  const [sendAfterCreate, setSendAfterCreate] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState('');

  const eligibleJobs = useMemo(() => {
    const list = (jobs as any[]) || [];
    return list
      .filter((j) => eligibleStatuses.has(j.status) && !invoicedJobIds.has(j.id))
      .filter((j) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          j.title?.toLowerCase().includes(q) ||
          j.client?.name?.toLowerCase().includes(q) ||
          j.address?.toLowerCase().includes(q)
        );
      });
  }, [jobs, query, invoicedJobIds]);

  const selectedJob = eligibleJobs.find((j) => j.id === selectedJobId);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!selectedJobId) return;
    setSubmitting(true);
    try {
      const { data } = await invoicesApi.createFromJob({
        jobId: selectedJobId,
        dueInDays,
        notes: notes.trim() || undefined,
        terms: terms.trim() || undefined,
      });
      const invoice = data?.data ?? data;
      qc.invalidateQueries({ queryKey: queryKeys.invoices });
      qc.invalidateQueries({ queryKey: queryKeys.jobs });

      if (sendAfterCreate && invoice?.id) {
        try {
          await invoicesApi.send(invoice.id);
          qc.invalidateQueries({ queryKey: queryKeys.invoices });
        } catch (err: any) {
          alert(
            `Invoice created but sending failed: ${err?.response?.data?.message || err?.message || 'Unknown error'}`,
          );
        }
      }

      onCreated?.(invoice);
      onClose();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create invoice from job</h2>
            <p className="mt-1 text-xs text-gray-500">
              Pick a completed job. We&apos;ll pre-fill line items from the original quote or job revenue.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Job</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, client, or address"
              className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <div className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-gray-200">
              {eligibleJobs.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No eligible jobs. Complete a job first, or it may already be invoiced.
                </div>
              ) : (
                eligibleJobs.slice(0, 50).map((job: any) => (
                  <button
                    key={job.id}
                    type="button"
                    onClick={() => setSelectedJobId(job.id)}
                    className={`flex w-full items-start justify-between gap-3 border-b border-gray-100 p-3 text-left text-sm transition-colors last:border-0 ${
                      selectedJobId === job.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-gray-900">{job.title || 'Untitled job'}</div>
                      <div className="truncate text-xs text-gray-500">
                        {job.client?.name || 'No client'}
                        {job.address ? ` · ${job.address}` : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        £{Number(job.revenue || 0).toFixed(2)}
                      </div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500">{job.status}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {selectedJob && (
            <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
              <div>
                Client: <strong>{selectedJob.client?.name || '—'}</strong>
              </div>
              {selectedJob.quote?.quoteNumber && (
                <div>
                  Source quote: <strong>{selectedJob.quote.quoteNumber}</strong>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Due in (days)</label>
              <input
                type="number"
                min={0}
                value={dueInDays}
                onChange={(e) => setDueInDays(Number(e.target.value) || 0)}
                className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={sendAfterCreate}
                  onChange={(e) => setSendAfterCreate(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Send to client immediately
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Visible to the client on the invoice"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Payment terms</label>
            <textarea
              rows={2}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !selectedJobId}
            className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:opacity-50"
          >
            {submitting
              ? 'Creating…'
              : sendAfterCreate
                ? 'Create & send'
                : 'Create draft'}
          </button>
        </div>
      </div>
    </div>
  );
}
